/**
 * JSON Diff Algorithm - Similar to Redux DevTools
 * 
 * This module provides functions to compute differences between two JSON objects/arrays
 * and returns a structured diff result that can be used for rendering.
 */

export type DiffType = 'added' | 'removed' | 'modified' | 'unchanged';

export interface DiffNode {
  key: string | number;
  type: DiffType;
  oldValue?: any;
  newValue?: any;
  children?: DiffNode[];
  // For arrays: track original index for better matching
  oldIndex?: number;
  newIndex?: number;
}

export interface DiffResult {
  type: DiffType;
  nodes: DiffNode[];
  // Metadata for rendering
  hasChanges: boolean;
}

/**
 * Deep equality check - optimized for primitive values and objects
 */
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return false;
  if (typeof a !== typeof b) return false;

  if (typeof a === 'object') {
    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i])) return false;
      }
      return true;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!keysB.includes(key)) return false;
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }

  return false;
}

/**
 * Check if value is a plain object (not array, null, etc.)
 */
function isPlainObject(value: any): boolean {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * Find the best matching item in old array for a new item
 * Uses a simple similarity heuristic
 */
function findBestMatch(newItem: any, oldArray: any[], usedIndices: Set<number>): number | null {
  let bestIndex: number | null = null;
  let bestScore = 0;

  for (let i = 0; i < oldArray.length; i++) {
    if (usedIndices.has(i)) continue;

    const oldItem = oldArray[i];
    
    // Exact match
    if (deepEqual(newItem, oldItem)) {
      return i;
    }

    // Calculate similarity score
    let score = 0;
    
    if (isPlainObject(newItem) && isPlainObject(oldItem)) {
      const newKeys = Object.keys(newItem);
      const oldKeys = Object.keys(oldItem);
      const commonKeys = newKeys.filter(k => oldKeys.includes(k));
      score = commonKeys.length / Math.max(newKeys.length, oldKeys.length);
    } else if (Array.isArray(newItem) && Array.isArray(oldItem)) {
      score = 0.3; // Arrays are less likely to match unless identical
    } else if (typeof newItem === typeof oldItem && newItem !== null && oldItem !== null) {
      score = 0.2; // Same type
    }

    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }

  // Only return match if similarity is high enough
  return bestScore > 0.5 ? bestIndex : null;
}

/**
 * Diff two arrays intelligently
 */
function diffArrays(oldArray: any[], newArray: any[]): DiffNode[] {
  const nodes: DiffNode[] = [];
  const usedOldIndices = new Set<number>();

  // First pass: find exact matches and updates
  newArray.forEach((newItem, newIndex) => {
    const oldIndex = findBestMatch(newItem, oldArray, usedOldIndices);

    if (oldIndex !== null && deepEqual(oldArray[oldIndex], newItem)) {
      // Unchanged - but we still track it for the old view
      usedOldIndices.add(oldIndex);
      nodes.push({
        key: newIndex,
        type: 'unchanged',
        oldValue: oldArray[oldIndex],
        newValue: newItem,
        oldIndex,
        newIndex,
      });
    } else if (oldIndex !== null) {
      // Modified
      usedOldIndices.add(oldIndex);
      const childDiff = computeDiff(oldArray[oldIndex], newItem);
      nodes.push({
        key: newIndex,
        type: 'modified',
        oldValue: oldArray[oldIndex],
        newValue: newItem,
        oldIndex,
        newIndex,
        children: childDiff.nodes.length > 0 ? childDiff.nodes : undefined,
      });
    } else {
      // Added
      nodes.push({
        key: newIndex,
        type: 'added',
        newValue: newItem,
        newIndex,
      });
    }
  });

  // Second pass: find removed items
  oldArray.forEach((oldItem, oldIndex) => {
    if (!usedOldIndices.has(oldIndex)) {
      nodes.push({
        key: oldIndex,
        type: 'removed',
        oldValue: oldItem,
        oldIndex,
      });
    }
  });

  return nodes;
}

/**
 * Diff two objects
 */
function diffObjects(oldObj: any, newObj: any): DiffNode[] {
  const nodes: DiffNode[] = [];
  const allKeys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);

  allKeys.forEach(key => {
    const oldVal = oldObj?.[key];
    const newVal = newObj?.[key];
    const hasOld = key in (oldObj || {});
    const hasNew = key in (newObj || {});

    if (!hasOld && hasNew) {
      // Added
      nodes.push({
        key,
        type: 'added',
        newValue: newVal,
      });
    } else if (hasOld && !hasNew) {
      // Removed
      nodes.push({
        key,
        type: 'removed',
        oldValue: oldVal,
      });
    } else if (!deepEqual(oldVal, newVal)) {
      // Modified - compute nested diff
      const childDiff = computeDiff(oldVal, newVal);
      nodes.push({
        key,
        type: 'modified',
        oldValue: oldVal,
        newValue: newVal,
        children: childDiff.nodes.length > 0 ? childDiff.nodes : undefined,
      });
    } else {
      // Unchanged
      nodes.push({
        key,
        type: 'unchanged',
        oldValue: oldVal,
        newValue: newVal,
      });
    }
  });

  return nodes;
}

/**
 * Main diff computation function
 */
export function computeDiff(oldValue: any, newValue: any): DiffResult {
  // Handle null/undefined
  if (oldValue === null || oldValue === undefined) {
    if (newValue === null || newValue === undefined) {
      return {type: 'unchanged', nodes: [], hasChanges: false};
    }
    return {type: 'added', nodes: [{key: 'root', type: 'added', newValue}], hasChanges: true};
  }

  if (newValue === null || newValue === undefined) {
    return {type: 'removed', nodes: [{key: 'root', type: 'removed', oldValue}], hasChanges: true};
  }

  // Handle primitives
  if (typeof oldValue !== 'object' || typeof newValue !== 'object') {
    if (deepEqual(oldValue, newValue)) {
      return {type: 'unchanged', nodes: [], hasChanges: false};
    }
    return {type: 'modified', nodes: [{key: 'root', type: 'modified', oldValue, newValue}], hasChanges: true};
  }

  // Handle arrays
  if (Array.isArray(oldValue) || Array.isArray(newValue)) {
    if (!Array.isArray(oldValue)) {
      return {type: 'modified', nodes: [{key: 'root', type: 'modified', oldValue, newValue}], hasChanges: true};
    }
    if (!Array.isArray(newValue)) {
      return {type: 'modified', nodes: [{key: 'root', type: 'modified', oldValue, newValue}], hasChanges: true};
    }
    
    const nodes = diffArrays(oldValue, newValue);
    const hasChanges = nodes.some(n => n.type !== 'unchanged');
    return {type: hasChanges ? 'modified' : 'unchanged', nodes, hasChanges};
  }

  // Handle objects
  if (isPlainObject(oldValue) && isPlainObject(newValue)) {
    const nodes = diffObjects(oldValue, newValue);
    const hasChanges = nodes.some(n => n.type !== 'unchanged');
    return {type: hasChanges ? 'modified' : 'unchanged', nodes, hasChanges};
  }

  // Type mismatch
  if (deepEqual(oldValue, newValue)) {
    return {type: 'unchanged', nodes: [], hasChanges: false};
  }
  return {type: 'modified', nodes: [{key: 'root', type: 'modified', oldValue, newValue}], hasChanges: true};
}

/**
 * Filter diff to show only changes (for new value view)
 */
export function filterChanges(diff: DiffResult): DiffResult {
  if (!diff.hasChanges) {
    return {type: 'unchanged', nodes: [], hasChanges: false};
  }

  const filterNodes = (nodes: DiffNode[]): DiffNode[] => {
    return nodes
      .filter(node => node.type !== 'unchanged')
      .map(node => {
        if (node.children && node.children.length > 0) {
          const filteredChildren = filterNodes(node.children);
          if (filteredChildren.length > 0) {
            return {...node, children: filteredChildren};
          }
        }
        return node;
      })
      .filter(node => {
        // Keep if it has changes or if it's a top-level change
        if (node.type !== 'unchanged') return true;
        if (node.children && node.children.length > 0) return true;
        return false;
      });
  };

  const filteredNodes = filterNodes(diff.nodes);
  return {
    type: diff.type,
    nodes: filteredNodes,
    hasChanges: filteredNodes.length > 0,
  };
}
